import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FieldArray, Form, Formik, useFormikContext } from "formik";
import { toast } from "react-toastify";

import { AddIcon, CloseIcon, PlusIcon } from "../../assets/icons";
import { Badge, Button, Loading, PageTitle, Section } from "../../components";
import { TextInput } from "../../components/forms";

import { supplierValidationSchema } from "../../schemas/suppliers";
import useSupplierService from "../../services/api/supplierService";
import "./SuppliersPage.scss";

function AddSupplierPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addSupplier, queryKey } = useSupplierService();
  const mutation = useMutation({
    mutationFn: addSupplier,
  });
  const [loading, setLoading] = useState(false);
  const initialValues = {
    name: "",
    companyName: "",
    phoneNumbers: [""],
    bankDetails: [
      {
        accountNumber: "",
        bankName: "",
        accountOwnerName: "",
        branch: "",
      },
    ],
  };
  const handleSubmit = (values) => {
    setLoading(true);
    mutation.mutate(
      { payload: values },
      {
        onSuccess: async (payload) => {
          await queryClient.resetQueries({
            queryKey: [queryKey],
          });
          toast.success(payload.message || "Supplier added successfully");
          navigate("/suppliers", { replace: true });
        },
        onSettled: () => {
          setLoading(false);
        },
        onError: (error) => {
          toast.error(
            error.response?.data?.payload?.message || "Failed to add supplier"
          );
        },
      }
    );
  };
  return (
    <div className="page add-supplier-page">
      <PageTitle text="Add New Supplier" prev="/suppliers" />

      <Section className="supplier-form">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={supplierValidationSchema}
          onSubmit={handleSubmit}
        >
          <Form>
            <TextInput name="name" label="Name" placeholder="Type here" />
            <TextInput
              name="companyName"
              label="Company Name"
              placeholder="Type here"
            />
            {/* DYNAMIC INPUTS */}
            <MobileNumberInput />
            <BankDetailsInput />
            {/* END DYNAMIC INPUTS */}
            <Button
              text="Add New Supplier"
              type="submit"
              className="add-new-supplier"
              icon={<AddIcon />}
            />
          </Form>
        </Formik>
      </Section>

      <Loading isLoading={loading} variant="fullScreen" />
    </div>
  );
}

export default AddSupplierPage;

function MobileNumberInput() {
  const { values } = useFormikContext();
  return (
    <FieldArray name="phoneNumbers">
      {({ push, remove }) => (
        <>
          <div className="input-group">
            {values.phoneNumbers.map((phoneNumber, index) => (
              <div className="mobile-number-input-area" key={index}>
                <div className="label-area">
                  <Badge text={`Mobile Number ${index + 1}`} />

                  {index > 0 ? (
                    <Badge
                      icon={<CloseIcon className="icon" />}
                      onClick={() => remove(index)}
                    />
                  ) : null}
                </div>
                <div className="input-area">
                  <span>+94</span>
                  <TextInput
                    name={`phoneNumbers[${index}]`}
                    placeholder="Type here"
                    className="mobile-input-field"
                  />
                </div>
              </div>
            ))}
          </div>
          <Button
            text="Add Phone Number"
            type="button"
            icon={<PlusIcon />}
            className="add-new-number"
            onClick={() => push("")}
          />
        </>
      )}
    </FieldArray>
  );
}

function BankDetailsInput() {
  const { values } = useFormikContext();
  return (
    <FieldArray name="bankDetails">
      {({ push, remove }) => (
        <>
          {values.bankDetails.map((bankDetail, index) => (
            <Section wrapperClassName="supplier-bank-details" key={index}>
              <div className="supplier-bank-details-title">
                <Badge text={`Bank Account ${index + 1}`} />
                {index > 0 ? (
                  <Badge
                    icon={<CloseIcon className="icon" />}
                    onClick={() => remove(index)}
                  />
                ) : null}
              </div>
              <div className="input-group">
                <TextInput
                  name={`bankDetails[${index}].accountNumber`}
                  label="Account Number"
                  placeholder="Type here"
                />
                <TextInput
                  name={`bankDetails[${index}].bankName`}
                  label="Bank Name"
                  placeholder="Type here"
                />
              </div>
              <div className="input-group">
                <TextInput
                  name={`bankDetails[${index}].accountOwnerName`}
                  label="Account Name"
                  placeholder="Type here"
                />
                <TextInput
                  name={`bankDetails[${index}].branch`}
                  label="Branch"
                  placeholder="Type here"
                />
              </div>
            </Section>
          ))}
          <Button
            text="Add Bank Account"
            type="button"
            icon={<PlusIcon />}
            className="add-new-number"
            onClick={() =>
              push({
                accountNumber: "",
                bankName: "",
                accountOwnerName: "",
                branch: "",
              })
            }
          />
        </>
      )}
    </FieldArray>
  );
}
